import React from 'react';
import styled from 'styled-components';
import {
  Tooltip,
  ButtonIcon,
  IconChartDown,
  IconChartUp,
} from '@spotify-internal/encore-creator-web';
import {
  gray60,
  failure,
  success,
  spacer4,
} from '@spotify-internal/encore-foundation';
import { TooltipTrigger } from '@mrkt/features/TooltipTrigger';
import { applyMetricSuffix } from '../../../../shared/lib/numberHelpers';

enum DeltaDirection {
  POSITIVE = 'POSITIVE',
  NEGATIVE = 'NEGATIVE',
  ZERO = 'ZERO',
}

type StatsDeltaProps = {
  delta: number;
  isDeltaPct: boolean;
  id: string;
  isDisabled?: boolean;
};

const DeltaButton = styled(ButtonIcon)`
  margin-left: ${spacer4};
  margin-top: -6px;
`;

const createDeltaObject = (delta: number) => {
  const roundedAbsValue = Math.floor(Math.abs(delta));

  let sign = DeltaDirection.POSITIVE;
  if (roundedAbsValue === 0) {
    sign = DeltaDirection.ZERO;
  }

  if (delta < 0 && roundedAbsValue !== 0) {
    sign = DeltaDirection.NEGATIVE;
  }

  return { sign, roundedAbsValue };
};

const getDeltaArrow = (sign: string) => {
  switch (sign) {
    case DeltaDirection.ZERO:
      return (
        <IconChartUp
          color={gray60}
          iconSize={16}
          aria-hidden="true"
          data-testid="upward"
        />
      );
    case DeltaDirection.NEGATIVE:
      return (
        <IconChartDown
          color={failure}
          iconSize={16}
          aria-hidden="true"
          data-testid="downward"
        />
      );
    default:
      return (
        <IconChartUp
          color={success}
          iconSize={16}
          aria-hidden="true"
          data-testid="upward"
        />
      );
  }
};

const createDeltaPctTooltip = (delta: {
  sign: string;
  roundedAbsValue: number;
}) => {
  let netChangeCopy = '';

  if (delta.sign === DeltaDirection.NEGATIVE) {
    netChangeCopy = 'decrease';
  }

  if (delta.sign === DeltaDirection.POSITIVE) {
    netChangeCopy = 'increase';
  }

  return `${applyMetricSuffix(
    delta.roundedAbsValue,
  )}% ${netChangeCopy} compared to last week.`;
};

const createFollowersTooltip = (delta: {
  sign: string;
  roundedAbsValue: number;
}) => {
  let netChangeCopy = '';

  if (delta.sign === DeltaDirection.NEGATIVE) {
    netChangeCopy = '-';
  }

  if (delta.sign === DeltaDirection.POSITIVE) {
    netChangeCopy = '+';
  }

  return `${netChangeCopy}${applyMetricSuffix(
    delta.roundedAbsValue,
  )} followers`;
};

export const StatsDelta = React.memo((props: StatsDeltaProps) => {
  const { delta, isDeltaPct, id, isDisabled } = props;
  const deltaInfo = createDeltaObject(delta);
  const deltaArrow = getDeltaArrow(deltaInfo.sign);

  if (isDisabled) {
    return <DeltaButton disabled>{deltaArrow}</DeltaButton>;
  }

  return (
    <TooltipTrigger
      tooltipId={`tooltip-${id}`}
      tooltip={
        <Tooltip>
          {isDeltaPct
            ? createDeltaPctTooltip(deltaInfo)
            : createFollowersTooltip(deltaInfo)}
        </Tooltip>
      }
      placement="left"
    >
      <DeltaButton aria-label="Change in listeners">{deltaArrow}</DeltaButton>
    </TooltipTrigger>
  );
});
